import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useGroupFilter } from 'pared/Routes/renderer/groupFilter'

import { useDateFilter } from '../../../dateFilter'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

type IMetricDataType = {
  businessQuarter: number
  businessMonth: number
  businessWeek: number
  businessWeekOfMonth: number
  metricData: Record<
    string,
    {
      name: string
      unit: string
      value: number
    }
  >
  endDate: string
}

type IDataType =
  | {
      trendLocationGroupMetricValues: {
        nodes: IMetricDataType[]
      }
    }
  | {
      trendLocationMetricValues: {
        nodes: IMetricDataType[]
      }
    }

interface IReturnType {
  period: string
  value: number | null
}

const query = gql`
  query TrendFinancialSalesData(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }

    trendLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

const priorYearQuery = gql`
  query TrendPriorFinancialSalesData(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }

    trendLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

export const winSalesConfigs = {
  period: 'string',
  value: 'price',
  valueLastYear: 'price',
} as const

const useWinSales = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()

  const groupBy = variables.farwestTimeinterval?.value[0][0] || 'business_month'

  const dateList = ((groupBy: string) => {
    if (groupBy === 'business_week')
      return variables.date?.getCalendar('week', 'year')?.ids || [[]]
    else if (groupBy === 'business_quarter') return ['Q1', 'Q2', 'Q3', 'Q4']
    else return variables.date?.getCalendar('period', 'year')?.ids || [[]]
  })(groupBy)

  const metrics = variables.selectOptions?.orderMode?.value[0][0]
  const iFilter = hasGroupBy
    ? {
        location_group_ids: groupFilter?.ids,
        intersected_location_group_ids: groupFilter?.intersectedIds,
        metrics: [metrics],
      }
    : {
        location_ids: groupFilter?.ids,
        metrics: [metrics],
      }

  const { data, loading: currentLoading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iGroupBy: groupBy,
      iFilter,
      hasGroupBy,
    },
    skip: !startDate || !endDate || !variables.date,
  })

  const { data: priorData, loading: priorLoading } = useQuery<IDataType>(
    priorYearQuery,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iGroupBy: groupBy,
        iFilter: {
          ...iFilter,
          use_yoy: true,
        },
        hasGroupBy,
      },
      skip: !startDate || !endDate || !variables.date,
    },
  )

  const loading = currentLoading && priorLoading

  return {
    data: useMemo((): IApiDataType => {
      const currentDataGroupBy = hasGroupBy
        ? data &&
          'trendLocationGroupMetricValues' in data &&
          data.trendLocationGroupMetricValues?.nodes
        : data &&
          'trendLocationMetricValues' in data &&
          data.trendLocationMetricValues?.nodes
      const priorDataGroupBy = hasGroupBy
        ? priorData &&
          'trendLocationGroupMetricValues' in priorData &&
          priorData.trendLocationGroupMetricValues?.nodes
        : priorData &&
          'trendLocationMetricValues' in priorData &&
          priorData.trendLocationMetricValues?.nodes

      if (!currentDataGroupBy || !priorDataGroupBy || !metrics) return null

      const metricData = currentDataGroupBy.map((item): IReturnType => {
        const periodEndDate = moment(item.endDate)
        const yesterday = moment().subtract(1, 'days')
        const period = ((groupBy: string) => {
          if (groupBy === 'business_week')
            return `P${item.businessMonth}W${item.businessWeekOfMonth}`
          else if (groupBy === 'business_quarter')
            return `Q${item.businessQuarter}`
          else return `P${item.businessMonth}`
        })(groupBy)

        if (
          ['DOLLAR', 'PERCENT', 'PERCENTAGE'].includes(
            item.metricData?.[metrics].unit,
          )
        )
          return {
            period,
            value: item.metricData?.[metrics].value
              ? item.metricData?.[metrics].value * 100
              : null,
          }

        return {
          period,
          value: yesterday.isAfter(periodEndDate)
            ? item.metricData?.[metrics].value || null
            : null,
        }
      })
      const priorMetricData = priorDataGroupBy.map((item): IReturnType => {
        const period = ((groupBy: string) => {
          if (groupBy === 'business_week')
            return `P${item.businessMonth}W${item.businessWeekOfMonth}`
          else if (groupBy === 'business_quarter')
            return `Q${item.businessQuarter}`
          else return `P${item.businessMonth}`
        })(groupBy)

        if (
          ['DOLLAR', 'PERCENT', 'PERCENTAGE'].includes(
            item.metricData?.[metrics].unit,
          )
        )
          return {
            period,
            value: item.metricData?.[metrics].value
              ? item.metricData?.[metrics].value * 100
              : null,
          }

        return {
          period,
          value: item.metricData?.[metrics].value || null,
        }
      })

      console.log(metricData)
      return dateList
        .map((date) => {
          const currentData = metricData.find((d) => d.period === date)
          const sameDaylyData = priorMetricData.find((d) => d.period === date)

          return {
            period: date,
            value: currentData?.value,
            valueLastYear: sameDaylyData?.value,
          }
        })
        .filter((d) => d.period)
    }, [data, priorData]),
    loading,
  }
}

export default useWinSales
